/**
 * CartKey Service
 * Simple service for managing cart key storage with compression
 */

import { StorageUtils } from '../utils/storage.utils';
import { ProductContext } from '../product/product.context';

interface CartData {
  cart: Array<{
    id: string;
    quantity: number;
    category: string;
    [key: string]: any;
  }>;
  lastAction?: {
    type: string;
    [key: string]: any;
  };
}

export class CartKeyService {
  private static readonly CART_KEY_NAME = 'cartKey';

  /**
   * Compress and set cart key value
   * @param value - The cart data object to compress and store
   * @param days - Days until expiration (default: 1)
   * @returns string - Compressed cart key
   */
  static compress(value: CartData, days: number = 1): string {
    try {
      // Create compressed format: Array of { "productId": quantity, "category": "..." }
      const compressedCart: Array<Record<string, any>> = [];
      
      // Build array with each product as an object
      if (value.cart.length > 0) {
        value.cart.forEach((item) => {
          // Skip items without an ID
          if (!item.id) {
            return;
          }
          
          const productObj: Record<string, any> = {
            [item.id]: item.quantity,
            category: item.category
          };
          compressedCart.push(productObj);
        });
      }
      
      // Add metadata (lastAction) to the structure
      const compressed = {
        cart: compressedCart,
        action: value.lastAction?.type || null
      };
      
      const compressedString = JSON.stringify(compressed);
      
      return compressedString;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get and decompress cart key value (synchronous)
   * @param encodedValue - The encoded/compressed cart key value  
   * @returns object | null - The decompressed cart data with full product details or null if not found
   */
  static decompress(encodedValue: any): any {
    try {
      const compressed = JSON.parse(encodedValue);
      
      if (!compressed || !compressed.cart) {
        return null;
      }
      
      // Get the full product list from ProductContext
      const cartProductsList = ProductContext.getCartProductsList();
      
      // If ProductContext is empty, create basic cart items with available data
      // This prevents the cart from appearing empty while products are loading
      if (!cartProductsList || cartProductsList.length === 0) {
        const basicCartItems: any[] = [];
        
        compressed.cart.forEach((item: Record<string, any>) => {
          const category = item.category;
          const productId = Object.keys(item).find(key => key !== 'category');
          
          if (productId) {
            const quantity = item[productId] as number;
            const expireDate = new Date();
            expireDate.setDate(expireDate.getDate() + 2);
            
            // Create minimal cart item without full product details
            basicCartItems.push({
              id: productId,
              sku: productId,
              name: productId, // Will be updated when products are scanned
              quantity: quantity,
              category: category || 'nvidia',
              activeTab: category || 'nvidia',
              productType: 'DIGITAL',
              listPrice: 0,
              unitPrice: 0,
              pricing: {
                listPriceWithQuantity: 0,
                salePriceWithQuantity: 0,
                unitPrice: 0
              },
              totalPrice: '0.00',
              productIsInStock: true,
              productIsAllowsBackorders: false,
              expires: expireDate,
              locale: 'en-us',
              minimumQuantity: 1,
              limitPerCustomer: null,
              availableQuantity: null,
              promo: null,
              isQuantity: true
            });
          }
        });
        
        // Return basic cart structure
        return {
          cart: basicCartItems,
          lastAction: compressed.action ? { type: compressed.action } : null,
          activeTab: basicCartItems.length > 0 ? basicCartItems[0].category : null,
          _needsProductDetails: true // Flag to indicate this needs full product data
        };
      }
      
      // Reconstruct cart items with full product details from the new array format
      const reconstructedCart: any[] = [];
      
      // Iterate through compressed cart array
      compressed.cart.forEach((item: Record<string, any>) => {
        // Extract product ID and quantity from the item
        // Each item is like: { "productId": quantity, "category": "..." }
        const category = item.category;
        
        // Find the product ID key (skip 'category' key)
        const productId = Object.keys(item).find(key => key !== 'category');
        
        if (!productId) {
          return; // Skip this item
        }
        
        const quantity = item[productId] as number;
        
        // Find the full product data from ProductContext
        const fullProduct = cartProductsList.find(
          (product: any) => String(product.id) === String(productId)
        );
        
        if (!fullProduct) {
          return; // Skip missing products
        }
        
        // Reconstruct full cart item
        const unitPriceNum = parseFloat(String(fullProduct.unitPrice)) || 0;
        const totalPrice = (quantity * unitPriceNum).toFixed(2);
        const availableQuantity = fullProduct.inventoryStatus?.availableQuantity || null;
        
        // Set expiration date (2 days from now)
        const expireDate = new Date();
        expireDate.setDate(expireDate.getDate() + 2);
        
        const reconstructedItem = {
          id: fullProduct.id,
          sku: fullProduct.sku,
          name: fullProduct.displayName && fullProduct.displayName.includes('-')
            ? fullProduct.displayName.split('-')[0]
            : fullProduct.displayName,
          isQuantity: true,
          quantity: quantity,
          category: category || fullProduct.category || 'nvidia',
          activeTab: category || fullProduct.category || 'nvidia',
          productType: fullProduct.productType,
          listPrice: fullProduct.salePriceWithQuantity,
          unitPrice: fullProduct.unitPrice,
          pricing: {
            listPriceWithQuantity: fullProduct.salePriceWithQuantity * quantity,
            salePriceWithQuantity: fullProduct.salePriceWithQuantity * quantity,
            unitPrice: fullProduct.unitPrice
          },
          totalPrice: totalPrice,
          productIsInStock: fullProduct.inventoryStatus?.productIsInStock || false,
          productIsAllowsBackorders: fullProduct.inventoryStatus?.productIsAllowsBackorders || false,
          expires: expireDate,
          locale: fullProduct.locale,
          minimumQuantity: fullProduct.minimumQuantity || 1,
          limitPerCustomer: fullProduct.maximumQuantity || null,
          availableQuantity: availableQuantity,
          promo: fullProduct.relatedProducts
        };
        
        reconstructedCart.push(reconstructedItem);
      });
      
      // Create the final decompressed result
      const decompressedResult = {
        cart: reconstructedCart,
        lastAction: compressed.action ? { type: compressed.action } : null,
        activeTab: reconstructedCart.length > 0 ? reconstructedCart[0].category : null
      };
      
      return decompressedResult;
    } catch (error) {
      return null;
    }
  }

  /**
   * Remove cart key
   * @returns boolean - true if successful, false otherwise
   */
  static remove(): boolean {
    try {
      StorageUtils.deleteCookie(this.CART_KEY_NAME);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if cart key exists
   * @returns boolean - true if exists, false otherwise
   */
  static exists(): boolean {
    const value = StorageUtils.getCookie(this.CART_KEY_NAME);
    return value !== null;
  }
}
